const STORAGE_KEY = 'dmarc_date_format'
const DEFAULT_FORMAT = 'yyyy/MM/dd'

export const DATE_FORMATS = [
  { value: 'yyyy/MM/dd', label: 'yyyy/MM/dd (2026/03/18)' },
  { value: 'dd/MM/yyyy', label: 'dd/MM/yyyy (18/03/2026)' },
  { value: 'MM/dd/yyyy', label: 'MM/dd/yyyy (03/18/2026)' },
  { value: 'dd.MM.yyyy', label: 'dd.MM.yyyy (18.03.2026)' },
  { value: 'yyyy-MM-dd', label: 'yyyy-MM-dd (2026-03-18)' },
] as const

export type DateFormatValue = (typeof DATE_FORMATS)[number]['value']

const VALID_FORMATS = DATE_FORMATS.map((f) => f.value as string)

export function getDateFormat(): DateFormatValue {
  const stored = localStorage.getItem(STORAGE_KEY)
  if (stored && VALID_FORMATS.includes(stored)) {
    return stored as DateFormatValue
  }
  return DEFAULT_FORMAT
}

export function setDateFormat(format: DateFormatValue): void {
  if (VALID_FORMATS.includes(format)) {
    localStorage.setItem(STORAGE_KEY, format)
  }
}

function toDate(input: string | Date | null | undefined): Date | null {
  if (!input) return null
  if (input instanceof Date) return isNaN(input.getTime()) ? null : input
  const d = new Date(input)
  return isNaN(d.getTime()) ? null : d
}

function padTwo(n: number): string {
  return n.toString().padStart(2, '0')
}

function applyFormat(d: Date, fmt: string): string {
  const yyyy = d.getFullYear().toString()
  const MM = padTwo(d.getMonth() + 1)
  const dd = padTwo(d.getDate())

  return fmt.replace('yyyy', yyyy).replace('MM', MM).replace('dd', dd)
}

/**
 * Format a date value using the user's preferred date format.
 * Returns '-' for null/undefined/invalid values.
 */
export function formatDate(
  input: string | Date | null | undefined,
  fallback = '-'
): string {
  const d = toDate(input)
  if (!d) return fallback
  return applyFormat(d, getDateFormat())
}

/**
 * Format a date+time value using the user's preferred date format + HH:mm.
 * Returns '-' for null/undefined/invalid values.
 */
export function formatDateTime(
  input: string | Date | null | undefined,
  fallback = '-'
): string {
  const d = toDate(input)
  if (!d) return fallback
  const datePart = applyFormat(d, getDateFormat())
  const timePart = `${padTwo(d.getHours())}:${padTwo(d.getMinutes())}`
  return `${datePart} ${timePart}`
}

/**
 * Compact relative time for inbox-style rows (e.g. "12m ago", "3h ago", "Yesterday", "4d ago").
 * Returns 'just now' for sub-minute freshness.
 */
export function relativeTime(dateStr: string): string {
  if (!dateStr) return '—'
  const date = new Date(dateStr)
  if (isNaN(date.getTime())) return '—'
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)
  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays === 1) return 'Yesterday'
  return `${diffDays}d ago`
}

/**
 * Humanise a "last seen" timestamp for Problems-tab surfaces (86caz42uc).
 *
 * Single source of truth for this specific relative-time shape ("Just now" /
 * "{n}h ago" / "Yesterday" / "{n}d ago" / formatted date beyond a week) —
 * previously duplicated between ProblemsPanel's per-IP "Last Report" column
 * and (as a raw, unformatted ISO string) the Problems-card top-level
 * timestamp. Both call sites now share this function.
 *
 * Absence or an unparseable value MUST render as '—', never as a raw ISO
 * string and never as a synthesized "now" — a missing `last_seen` is not
 * the same fact as "seen right now", and rendering it that way is exactly
 * the fabricated-timestamp hazard this fix closes (see useProblems.ts).
 */
export function formatLastSeen(
  input: string | null | undefined,
  fallback = '—'
): string {
  if (!input) return fallback
  const d = new Date(input)
  if (isNaN(d.getTime())) return fallback
  const now = Date.now()
  const diffHours = Math.floor((now - d.getTime()) / 3600000)
  if (diffHours < 1) return 'Just now'
  if (diffHours < 24) return `${diffHours}h ago`
  const diffDays = Math.floor(diffHours / 24)
  if (diffDays === 1) return 'Yesterday'
  if (diffDays < 7) return `${diffDays}d ago`
  return formatDate(d)
}

/**
 * Format a date as relative time (Today, Yesterday, Xh ago, Xd ago)
 * or fall back to formatted date for older dates.
 * Returns 'Never' for null/undefined.
 */
export function formatRelativeDate(
  input: string | Date | null | undefined,
  fallback = 'Never'
): string {
  const d = toDate(input)
  if (!d) return fallback

  const now = new Date()
  const diffMs = now.getTime() - d.getTime()
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  const isToday =
    d.getDate() === now.getDate() &&
    d.getMonth() === now.getMonth() &&
    d.getFullYear() === now.getFullYear()

  if (isToday) {
    if (diffHours < 1) return 'Today'
    return `${diffHours}h ago`
  }

  const yesterday = new Date(now)
  yesterday.setDate(yesterday.getDate() - 1)
  const isYesterday =
    d.getDate() === yesterday.getDate() &&
    d.getMonth() === yesterday.getMonth() &&
    d.getFullYear() === yesterday.getFullYear()

  if (isYesterday) return 'Yesterday'

  if (diffDays <= 7) return `${diffDays}d ago`

  return formatDate(d)
}
